import React from 'react'
import Head from 'next/head'
import cn from 'classnames'
import { Waypoint } from 'react-waypoint'
import { plainToInstance } from 'class-transformer'

import { useDrawer } from '@hooks/contexts/DrawerContext/DrawerContext'
import { useAlert } from '@hooks/useAlert'
import { TopRankFilter } from '@components/TopRankFilter'
import { TopRankCard } from '@components/TopRankCard/TopRankCard'
import { chartNumbers } from '@constants/chartNumbers'
import { Container } from '@components/Container'
import NoData from '@components/NoData'
import { BookStatusEnum } from '@interfaces/BookStatusEnum'
import { StoryDetailReaderType } from '@models/story/StoryDetailReaderType'
import { TopRankTemplateProps } from './interface'

export function TopRankTemplate({
  query,
  books,
  title,
  seoTitle,
  limit,
  isLoading,
  dateOfWeek,
  disableNext,
  disablePrev,
  isLoadingToggleBookLike,
  type,
  handleCategoryChange,
  handleBookTypeChange,
  nextWeek,
  prevWeek,
  handleLikeBook,
  handleFetchNextPage,
}: TopRankTemplateProps) {
  const storyDrawer = useDrawer({ drawer: 'story' })
  const alert = useAlert()
  return (
    <Container>
      <Head>
        <title>{seoTitle}</title>
      </Head>
      <TopRankFilter
        dateOfWeek={dateOfWeek}
        title={title}
        type={type}
        category={query.categoryId}
        storyType={query.bookType}
        disableNext={disableNext}
        disablePrev={disablePrev}
        isHomePage={false}
        handleChangeCategory={handleCategoryChange}
        handleChangeStoryType={handleBookTypeChange}
        handleClickNextWeek={nextWeek}
        handleClickPrevWeek={prevWeek}
      />
      <div className='grid grid-cols-2 mobile:grid-cols-1 gap-y-[30px] tablet-mobile:gap-y-[20px] mt-[50px] tablet-mobile:mt-[30px] mx-auto mobile:px-[20px]'>
        {books?.pages.map((page, i) =>
          page?.data.map((item, index) => (
            <div
              key={item.id}
              className={cn('relative')}
              onClick={() => {
                if (item.book.isBanned) return

                if (
                  item.book.status !== BookStatusEnum.PUBLISHED ||
                  item.book.deletedAt
                ) {
                  alert.warning('หนังสือเรื่องนี้ไม่เผยแพร่/ถูกลบแล้ว')
                  return
                }

                storyDrawer.show({
                  bookId: item.book.id,
                  initialData: plainToInstance(StoryDetailReaderType, {
                    id: item.book.id,
                    coverImgPath: item.book.coverImgPath,
                    title: item.book.title,
                    writingType: item.book.writingType,
                    publishedAt: item.book.publishedAt,
                    cover: item.book.cover,
                    viewCount: item.book.viewCount,
                    commentCount: item.book.commentCount,
                    likeCount: item.book.likeCount,
                    publishedChapterCount: item.book.publishedChapterCount,
                    penName: item.book.penName,
                    category: item.book.category,
                    bookType: item.book.bookType,
                    tags: [],
                    rating: {
                      name: '',
                    },
                    isTranslated: item.book.isTranslated,
                    isEnded: item.book.isEnded,
                    isEpub: item.book.isEpub,
                    isPdf: item.book.isPdf,
                    isLiked: item.book.isLiked,
                  }),
                })
              }}
            >
              <div className='absolute top-0 left-0'>
                {limit * i + index < 10 ? (
                  chartNumbers[index]
                ) : (
                  <div
                    className={cn(
                      'px-[28px] py-[50px]  tablet-mobile:py-[34px] text-primary text-3xl font-bold',
                      {
                        'tablet-mobile:px-[0px]': limit * i + index + 1 > 99,
                        'tablet-mobile:px-[7px]': limit * i + index + 1 <= 99,
                      }
                    )}
                  >
                    {limit * i + index + 1}
                  </div>
                )}
              </div>
              <TopRankCard
                key={item.id}
                bookDetail={item}
                className='ml-[100px] tablet-mobile:ml-[50px] relative'
                mode='rank'
                slug={item.book?.penName?.user?.profilePageSlug}
                isLoadingToggleBookLike={isLoadingToggleBookLike}
                handleLikeBook={handleLikeBook}
              />
            </div>
          ))
        )}
      </div>
      {books && books.pages.length && books.pages[0].data.length === 0 ? (
        <NoData />
      ) : books && books.pages.length && books.pages[0].data.length > 0 ? (
        <Waypoint onEnter={handleFetchNextPage} />
      ) : null}
    </Container>
  )
}

export default TopRankTemplate
